import React, { useState } from "react";
import "./index.css";
import { FaEdit, FaTrash, FaEye, FaComment } from "react-icons/fa";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"; // use as MovertIcon
import SensorOccupiedIcon from "@mui/icons-material/Sensors"; // sensor icon
import { Tooltip } from "@mui/material";
import { TbArrowBarDown } from 'react-icons/tb';
import { TbArrowBarToUp } from 'react-icons/tb';

const DynamicTable = ({
  columns,
  data,
  onEdit,
  onView,
  onDelete,
  onComment,
  view = true,
  edit = true,
  deleteAction = true,
  isLoading = false,
  multipleaction = true,
  fiter,
  totalRowsProp,
  currentPageProp,
  onPageChange,
  rowsPerPageProp = 10,
  serverSide = false,
  onSort,
  sortConfig: externalSortConfig,
}) => {
  const [internalPage, setInternalPage] = useState(1);
  const currentPage = serverSide ? (currentPageProp || 1) : internalPage;
  const rowsPerPage = serverSide ? (rowsPerPageProp || 10) : 10;

  const [filterText, setFilterText] = useState("");
  const [internalSortConfig, setInternalSortConfig] = useState({ key: null, direction: "asc" });
  const sortConfig = externalSortConfig || internalSortConfig;
  const setSortConfig = onSort || setInternalSortConfig;
  const [expandedRows, setExpandedRows] = useState([]);
  const [movertToggles, setMovertToggles] = useState({});

  const toggleExpand = (id) =>
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleMovert = (id) =>
    setMovertToggles((prev) => ({ ...prev, [id]: !prev[id] }));

  // Filter columns based on data presence for specific keys
  const filteredColumns = columns.filter(col => {
    if (['reason', 'cancel_reason', 'hold_date', 'reason_hours'].includes(col.key)) {
      return data.some(row => row[col.key] && String(row[col.key]).trim() !== '');
    }
    return true;
  });

  const truncate = (desc) =>
    desc?.length > 15 ? `${desc.substring(0, 15)}...` : desc;

  const sortedData = [...data].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    if (typeof aValue === "string") {
      return sortConfig.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
  });

  const filteredData = sortedData.filter((row) =>
    filteredColumns.some(
      (col) =>
        col.key !== "action" &&
        row[col.key] != null &&
        row[col.key].toString().toLowerCase().includes(filterText.toLowerCase())
    )
  );

  const totalRows = serverSide ? totalRowsProp : filteredData.length;
  const totalPages = Math.ceil(totalRows / (rowsPerPage || 10));
  const start = (currentPage - 1) * rowsPerPage;
  const currentData = serverSide ? data : filteredData.slice(start, start + rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      if (serverSide) {
        onPageChange(page);
      } else {
        setInternalPage(page);
      }
    }
  };

  const handleSort = (key) => {
    if (key === "action") return;
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
    if (serverSide) {
      onPageChange(1);
    } else {
      setInternalPage(1);
    }
  };

  const renderSkeleton = () =>
    Array.from({ length: rowsPerPage }).map((_, idx) => (
      <tr key={idx}>
        {filteredColumns.map((_, cidx) => (
          <td key={cidx}>
            <div className="skeleton-line" />
          </td>
        ))}
      </tr>
    ));

  return (
    <div className="table-container">
      <div className="filter-container">
        {fiter && (
          <input
            className="filter-input"
            placeholder="Search..."
            value={filterText}
            disabled={isLoading}
            onChange={(e) => {
              setFilterText(e.target.value);
              if (serverSide) {
                onPageChange(1);
              } else {
                setInternalPage(1);
              }
            }}
          />
        )}
      </div>

      <div className="table-responsive">
        <table className="reusable-table">
          <thead>
            <tr>
              {filteredColumns.map((col, idx) => (
                <th
                  key={idx}
                  className={col.key !== "action" ? "sortable" : ""}
                  onClick={() => handleSort(col.key)}
                >
                  <div className="header-content">
                    {col.header}
                    {!isLoading && sortConfig.key === col.key && (
                      <span className="sort-icon">
                        {sortConfig.direction === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              renderSkeleton()
            ) : currentData.length > 0 ? (
              currentData.map((row, rIdx) => (
                <React.Fragment key={row.id}>
                  <tr className="parent-row">
                    {filteredColumns.map((col, cIdx) => (
                      <td key={cIdx}>
                        {col.key === "action" ? (
                          <div className="action-buttons">
                            {edit && (
                              <FaEdit
                                className="action-icon edit-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(row.id, row);
                                }}
                                title="Edit"
                              />
                            )}
                            {view && (
                              <FaEye
                                className="action-icon view-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onView(row.id, row);
                                }}
                                title="View"
                              />
                            )}
                            {deleteAction && (
                              <FaTrash
                                className="action-icon delete-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(row.id, row);
                                }}
                                title="Delete"
                              />
                            )}

                            {/* Movert (DragIndicatorIcon) */}
                            {multipleaction && (
                              <DragIndicatorIcon
                                className="action-icon movert-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleMovert(row.id);
                                }}
                                title="Movert"
                              />
                            )}

                            {/* Show Comment & Sensor when toggled */}
                            {movertToggles[row.id] && (
                              <>
                                <FaComment
                                  className="action-icon comment-icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onComment(row.id, row);
                                  }}
                                  title="Comment"
                                />
                                <SensorOccupiedIcon
                                  className="action-icon sensor-icon"
                                  title="Sensor Occupied"
                                />
                              </>
                            )}

                            {Array.isArray(row.subtasks) &&
                              row.subtasks.length > 0 && (
                                <span
                                  className="dropdown-icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand(row.id);
                                  }}
                                  title={
                                    expandedRows.includes(row.id)
                                      ? "Collapse"
                                      : "Expand"
                                  }
                                >
                                  {expandedRows.includes(row.id) ? <TbArrowBarToUp /> : <TbArrowBarDown />}

                                </span>
                              )}
                          </div>
                        ) : (
                          <Tooltip title={
                            <span style={{ fontSize: "10px" }}>
                              {col.key === "actual_hours" && row.hold_date
                                ? (Number(row.reason_hours || 0) + Number(row.actual_hours || 0)).toString()
                                : row[col.key]}
                            </span>
                          } arrow>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", maxWidth: "100%", fontSize: "12px" }}>
                              {col.key === "assigned_hours" && (!row["actual_hours"] || row["actual_hours"] === "" || row["actual_hours"] === null || row["actual_hours"] === undefined || parseFloat(row["actual_hours"]) === 0)
                                ? "-"
                                : truncate(
                                  col.key === "actual_hours" && row.hold_date
                                    ? (Number(row.reason_hours || 0) + Number(row.actual_hours || 0)).toString()
                                    : row[col.key]
                                ) ?? "-"}
                            </span>
                          </Tooltip>
                        )}
                      </td>
                    ))}
                  </tr>

                  {expandedRows.includes(row.id) &&
                    row.subtasks.map((sub, sIdx) => (
                      <tr key={sub.id} className="subtask-row">
                        {filteredColumns.map((col, cIdx) => (
                          <td key={cIdx}>
                            {col.key === "action" ? (
                              <div className="action-buttons">
                                {edit && (
                                  <FaEdit
                                    className="action-icon edit-icon"
                                    onClick={() => onEdit(sub.id, sub)}
                                    title="Edit Subtask"
                                  />
                                )}
                                {view && (
                                  <FaEye
                                    className="action-icon view-icon"
                                    onClick={() => onView(sub.id, sub)}
                                    title="View Subtask"
                                  />
                                )}
                                {deleteAction && (
                                  <FaTrash
                                    className="action-icon delete-icon"
                                    onClick={() => onDelete(sub.id, sub)}
                                    title="Delete Subtask"
                                  />
                                )}
                                {multipleaction && (
                                  <DragIndicatorIcon
                                    className="action-icon movert-icon"
                                    onClick={() => toggleMovert(sub.id)}
                                    title="Movert Subtask"
                                  />
                                )}
                                {movertToggles[sub.id] && (
                                  <>
                                    <FaComment
                                      className="action-icon comment-icon"
                                      onClick={() => onComment(sub.id, sub)}
                                      title="Comment"
                                    />
                                    <SensorOccupiedIcon
                                      className="action-icon sensor-icon"
                                      title="Sensor Occupied"
                                    />
                                  </>
                                )}
                              </div>
                            ) : (
                              <Tooltip
                                title={
                                  <span style={{ fontSize: "10px" }}>
                                    {col.key === "actual_hours" && sub.hold_date
                                      ? (Number(sub.reason_hours || 0) + Number(sub.actual_hours || 0)).toString()
                                      : sub[col.key]}
                                  </span>
                                }
                                arrow
                              >
                                <span
                                  className="subtask-cell"
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "inline-block",
                                    maxWidth: "100%",
                                    fontSize: "12px"
                                  }}
                                >
                                  {col.key === "assigned_hours" && (!sub["actual_hours"] || sub["actual_hours"] === "" || sub["actual_hours"] === null || sub["actual_hours"] === undefined || parseFloat(sub["actual_hours"]) === 0)
                                    ? "-"
                                    : truncate(
                                      col.key === "actual_hours" && sub.hold_date
                                        ? (Number(sub.reason_hours || 0) + Number(sub.actual_hours || 0)).toString()
                                        : sub[col.key] ?? "-"
                                    )}
                                </span>
                              </Tooltip>

                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={filteredColumns.length} className="no-data">
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!isLoading && totalRows > 0 && (
        <div className="pagination-container">
          <button
            className="pagination-button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .slice(
              Math.max(0, currentPage - 3),
              Math.min(totalPages, currentPage + 2)
            )
            .map((p) => (
              <button
                key={p}
                className={`pagination-button ${p === currentPage ? "active" : ""
                  }`}
                onClick={() => handlePageChange(p)}
              >
                {p}
              </button>
            ))}
          <button
            className="pagination-button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
        </div>
      )}
    </div>
  );
};

export default DynamicTable;
